import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CustomError } from 'src/lib/error';
import { getBusinessProfile, updateBusinessProfile } from 'src/services/business-profile/business-profile.service';
import { RootState } from '.';
import { TBusinessProfile } from 'src/types/business-profile.type';
import logger from 'src/logger';
import { getSessionIdentifier } from 'src/helpers/session.helpers';

type TProfileStore = TBusinessProfile & { lastFetchTs: EpochTimeStamp; isAIUser: boolean };

const expiry = 1000 * 60 * 30;

const initialState: TProfileStore = {
  name: '',
  description: '',
  lastFetchTs: 0,
  isAIUser: false,
};

const businessProfileSlice = createSlice({
  name: 'businessProfile',
  initialState,
  reducers: {
    updateBusinessProfileStore: (state: TProfileStore, action: PayloadAction<TProfileStore>) => {
      return { ...state, ...action.payload };
    },
    resetBusinessPorfileStore: () => initialState,
  },
});

export const { updateBusinessProfileStore, resetBusinessPorfileStore } = businessProfileSlice.actions;

export const businessProfileSelector = (state: RootState) => state.businessProfile;

export default businessProfileSlice.reducer;

export const getBusinessProfileAction = createAsyncThunk<TBusinessProfile, { forceFetch?: boolean } | undefined>(
  'get/business-profile',
  async ({ forceFetch } = { forceFetch: false }, { getState, dispatch }) => {
    const { session, businessProfile } = getState() as RootState;
    try {
      const { lastFetchTs, ...rest } = businessProfile;
      if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });
      if (forceFetch || lastFetchTs === 0 || Date.now() - lastFetchTs > expiry) {
        const profile = await getBusinessProfile(getSessionIdentifier(session));
        dispatch(updateBusinessProfileStore({ ...profile, lastFetchTs: Date.now() }));
        return profile;
      }
      return rest as TBusinessProfile;
    } catch (err) {
      throw err;
    }
  },
);

// To be used to store business information entered if user chose to build Manually
export const updateBusinessProfileAction = createAsyncThunk(
  'update/business-profile',
  async (businessProfile: TBusinessProfile, { getState, dispatch }) => {
    const { session } = getState() as RootState;
    try {
      if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });
      const profile = { ...businessProfile, details: undefined, isAIUser: false };
      await updateBusinessProfile(getSessionIdentifier(session), profile);
      dispatch(updateBusinessProfileStore({ ...profile, lastFetchTs: Date.now() }));
    } catch (err) {
      logger.reportError(err, {
        isAI: true,
      });
    }
  },
);
